import type { RealTimeTranscriptLine, TranscriptHighlight } from '../types';
import buildTextSegments from './buildTextSegments';
import { formatTimestamp, formatUtcDateTime, splitSpeaker } from './formatters';

const hasLineIdentity = (line: Partial<RealTimeTranscriptLine>) =>
  line.lineId != null || line.lineNumber != null || line.pageLineNumber != null;

type ExportTxtOptions = {
  showTimestamps?: boolean;
};

export default function exportTranscriptTxt(
  displayLines: RealTimeTranscriptLine[],
  highlights: TranscriptHighlight[],
  title?: string,
  options?: ExportTxtOptions,
): void {
  const showTimestamps = options?.showTimestamps !== false;
  const headerText = title || 'Real-time transcript';
  const parts: string[] = [headerText, `Exported: ${new Date().toLocaleString()}`, ''];

  // Build highlight lookup by lineId (supports multi-line highlights)
  const highlightsByLine = new Map<string, TranscriptHighlight[]>();
  for (const hl of highlights) {
    const lineSelections =
      hl.selections && hl.selections.length > 0
        ? hl.selections
        : [
            {
              lineId: hl.lineId,
              startOffset: hl.startOffset,
              endOffset: hl.endOffset,
              text: hl.text,
              lineNumberAtCreate: hl.lineNumberAtCreate,
            },
          ];

    for (const sel of lineSelections) {
      const existing = highlightsByLine.get(sel.lineId);
      const perLineView = {
        ...hl,
        lineId: sel.lineId,
        startOffset: sel.startOffset,
        endOffset: sel.endOffset,
        text: sel.text,
      };
      if (existing) {
        existing.push(perLineView);
      } else {
        highlightsByLine.set(sel.lineId, [perLineView]);
      }
    }
  }

  let currentPage: number | undefined;

  for (const line of displayLines) {
    // Skip non-content lines
    if (!(typeof line.text === 'string' && line.text.length > 0) && !hasLineIdentity(line)) {
      continue;
    }

    // Detect implicit page change
    if (line.pageNumber != null && line.pageNumber !== currentPage) {
      currentPage = line.pageNumber;
      parts.push('', `--- PAGE ${currentPage} ---`, '');
    }

    const timeLabel = line.finalizedAtUtc
      ? formatUtcDateTime(line.finalizedAtUtc)
      : formatTimestamp(line.timestamp);
    const { speaker, content } = splitSpeaker(line.text);
    const lineHighlights = highlightsByLine.get(line.id) || [];

    const rawText = speaker ? `${speaker}: ${content}` : line.text;

    let textPart = rawText;
    if (lineHighlights.length > 0) {
      const segments = buildTextSegments(rawText, lineHighlights);
      textPart = segments
        .map((seg) => (seg.highlightId ? `[[HIGHLIGHT]]${seg.text}[[/HIGHLIGHT]]` : seg.text))
        .join('');
    }

    // Prepend line number (right-justified, 4 chars)
    const lineNum = line.pageLineNumber ?? line.lineNumber;
    const lineNumStr = lineNum != null ? String(lineNum).padStart(4, ' ') : '    ';

    parts.push(
      showTimestamps ? `${lineNumStr} [${timeLabel}] ${textPart}` : `${lineNumStr} ${textPart}`,
    );

    // Append notes with comments
    const notesForLine = lineHighlights.filter(
      (h) => (h.note && h.note.trim()) || (h.comments && h.comments.length > 0),
    );
    for (const hl of notesForLine) {
      const entries: Array<{ author: string; text: string }> = [];
      if (hl.note && hl.note.trim()) {
        entries.push({ author: hl.createdByName ?? '', text: hl.note });
      }
      if (hl.comments && hl.comments.length > 0) {
        for (const c of hl.comments) {
          entries.push({ author: c.createdBy?.name ?? '', text: c.commentText });
        }
      }
      if (entries.length > 0) {
        parts.push('         Notes:');
        for (const entry of entries) {
          parts.push('');
          if (entry.author) parts.push(`           ${entry.author}`);
          parts.push(`           "${entry.text}"`);
        }
      }
    }
  }

  const blob = new Blob([parts.join('\n')], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'transcript-export.txt';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
